import { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const cls = "w-full h-10 rounded-md bg-slate-900 border border-slate-700 px-3 text-sm focus:outline-none focus:border-[#1074BA]";
export const strong = (p) => p.length >= 10 && /[A-Za-z]/.test(p) && /\d/.test(p);

export function PasswordFields({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const mismatch = value.confirm && value.next !== value.confirm;
  return (
    <>
      <input type="password" className={cls} placeholder="New password" value={value.next} onChange={set("next")} data-testid="pw-new-input" autoComplete="new-password" />
      <input type="password" className={cls} placeholder="Confirm new password" value={value.confirm} onChange={set("confirm")} data-testid="pw-confirm-input" autoComplete="new-password" />
      <p className={`text-[11px] ${value.next && !strong(value.next) ? "text-red-400" : "text-slate-500"}`}>At least 10 characters, with a letter and a number.</p>
      {mismatch && <p className="text-[11px] text-red-400" data-testid="pw-mismatch">Passwords don't match</p>}
    </>
  );
}

export function ChangePassword() {
  const { setSession } = useAuth();
  const [f, setF] = useState({ current: "", next: "", confirm: "", code: "" });
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = f.current && f.code.length === 6 && strong(f.next) && f.next === f.confirm;

  const sendCode = async () => {
    setBusy(true);
    try { setSentTo((await api.post("/auth/password/code")).data.email); toast.success("Code sent to your email"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password/change", { current_password: f.current, new_password: f.next, code: f.code });
      setSession(data);
      setF({ current: "", next: "", confirm: "", code: "" });
      setSentTo("");
      toast.success("Password changed. Other devices have been signed out.");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="bg-[#0F172A]/80 border border-slate-800 rounded-lg p-6 max-w-md space-y-3" data-testid="change-password-form">
      <h3 className="font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#F58B23]" />Change password</h3>
      <input type="password" className={cls} placeholder="Current password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} data-testid="pw-current-input" autoComplete="current-password" />
      <PasswordFields value={f} onChange={setF} />
      <div className="flex gap-2">
        <input inputMode="numeric" maxLength={6} className={cls} placeholder="6-digit email code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, "") })} data-testid="pw-code-input" />
        <button type="button" onClick={sendCode} disabled={busy} data-testid="pw-send-code" className="shrink-0 px-3 h-10 rounded-md border border-slate-700 hover:border-[#F58B23] text-xs disabled:opacity-50">{sentTo ? "Resend code" : "Email me a code"}</button>
      </div>
      {sentTo && <p className="text-[11px] text-slate-400" data-testid="pw-code-sent">Code sent to {sentTo}. It expires in 10 minutes.</p>}
      <button disabled={!ok || busy} data-testid="pw-submit" className="w-full h-10 rounded-md bg-[#1074BA] hover:bg-[#0D629E] text-white text-sm font-medium disabled:opacity-50">{busy ? "Please wait…" : "Update password"}</button>
    </form>
  );
}
